import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  InputLabel,
  Select,
  TextField,
  Grid,
  Typography,
  Button,
  MenuItem,
  FormControl,
} from "@mui/material";
import MDBox from "components/MDBox";

const Create = ({ data, saveRecord, roles, organizations }) => {
  const [formData, setFormData] = useState({
    id: "",
    username: "",
    email: "",
    password: "",
    role: "", // Added role field
    organizations: [], // Added organizations field
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (data) {
      setFormData({
        id: data.id ?? "",
        username: data.username ?? "",
        email: data.email ?? "",
        password: "",
        role: data.roleIds ?? "", // Populate role if available
        organizations: data.organizationIds ?? [], // Populate organizations if available
      });
    } else {
      setFormData({
        id: "",
        username: "",
        email: "",
        password: "",
        role: "", // Initialize role
        organizations: [], // Initialize organizations
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleOrganizationChange = (e) => {
    const { value } = e.target;
    setFormData({ ...formData, organizations: value });
  };

  const validateForm = () => {
    let tempErrors = {};

    if (!formData.username.trim()) tempErrors.username = "Name is required";
    if (!formData.email.trim()) tempErrors.email = "Email is required";
    else if (!/^\S+@\S+\.\S+$/.test(formData.email))
      tempErrors.email = "Invalid email format";
    if (!formData.role) tempErrors.role = "Role is required";
    if (formData.organizations.length === 0)
      tempErrors.organizations = "At least one organization is required";

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      saveRecord(formData);
    }
  };

  const handleReset = () => {
    setFormData({
      id: "",
      username: "",
      email: "",
      password: "",
      role: "",
      organizations: [],
    });
    setErrors({});
  };

  return (
    <MDBox
      container
      sx={{
        minHeight: "75vh",
        p: 0,
      }}
    >
      <MDBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          bgcolor: "background.paper",
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography variant="h5" component="h1" align="center" gutterBottom>
          User Form
        </Typography>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                name="username"
                variant="outlined"
                required
                value={formData.username}
                onChange={handleChange}
                error={!!errors.username}
                helperText={errors.username}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                variant="outlined"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password (Optional)"
                name="password"
                variant="outlined"
                type="password"
                value={formData.password}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.role}>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  sx={{
                    height: 40, // Set your desired height here
                    '& .MuiInputBase-input': {
                      height: '100%',
                      padding: '10px 14px',
                    },
                  }}
                  required
                >
                  {roles.map((role) => (
                    <MenuItem key={role.id} value={role.id}>
                      {role.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth error={!!errors.organizations}>
                <InputLabel>Organizations</InputLabel>
                <Select
                  label="Organizations"
                  name="organizations"
                  multiple
                  value={formData.organizations}
                  onChange={handleOrganizationChange}
                  required
                  sx={{
                    height: 40, // Set your desired height here
                    '& .MuiInputBase-input': {
                      height: '100%',
                      padding: '10px 14px',
                    },
                  }}
                  renderValue={(selected) => selected.join(", ")} // Display selected organizations as a comma-separated list
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.organizations && (
                  <Typography variant="caption" color="error">
                    {errors.organizations}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} display="flex" justifyContent="space-between">
              <Button type="button" variant="outlined" color="secondary" onClick={handleReset}>
                Reset
              </Button>
              <Button type="submit" variant="contained" color="primary">
                Submit
              </Button>
            </Grid>
          </Grid>
        </form>
      </MDBox>
    </MDBox>
  );
};

Create.propTypes = {
  saveRecord: PropTypes.func.isRequired,
  data: PropTypes.object,
  roles: PropTypes.array.isRequired,
  organizations: PropTypes.array.isRequired,
};

export default Create;